(() => {
  'use strict';
  if (window.exoAssetProtectionReady || window.Shopify?.designMode) return;

  const notice = document.querySelector('[data-exo-asset-notice]');
  if (!notice) return;
  window.exoAssetProtectionReady = true;

  const pageButton = document.querySelector('[data-exo-share-page]');
  const existingButton = notice.querySelector('[data-exo-share-existing]');
  const nativeButton = notice.querySelector('[data-exo-share-native]');
  const copyButton = notice.querySelector('[data-exo-share-copy]');
  const closeButton = notice.querySelector('[data-exo-asset-close]');
  const manual = notice.querySelector('[data-exo-share-manual]');
  const urlInput = notice.querySelector('[data-exo-share-url]');
  const status = notice.querySelector('[data-exo-share-status]');
  const noticeHome = notice.parentElement;
  const mediaSelector = 'img, picture, video, canvas, svg, model-viewer';
  const editableSelector = 'input, textarea, select, [role="textbox"], [data-exo-allow-native-context]';
  const shareRoots = '[data-exo-product-trust-share][data-social-ready="true"], [data-gameday-social-share][data-social-ready="true"]';
  let source = null;
  let returnFocus = null;
  let viewerGesture = null;

  // The installed floating contact app supplies this bare domain as a relative
  // href. Normalize just that known destination, including delayed app mounts.
  const repairContactLinks = () => {
    document.querySelectorAll('.hmak-float-button a[href]').forEach((link) => {
      if (/^(?:www\.)?exolox\.com\/pages\/contact\/?$/.test(link.getAttribute('href'))) {
        link.setAttribute('href', '/pages/contact');
      }
    });
  };
  repairContactLinks();
  new MutationObserver(repairContactLinks).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });

  const elementPath = (event) => event.composedPath().filter((item) => item instanceof Element);
  const allowsNative = (event) => elementPath(event).some((el) =>
    el.isContentEditable || el.matches(editableSelector)
  );
  const visible = (el) => !!el && !el.disabled && el.getClientRects().length > 0;

  // Reuse the established design/product share handlers and their tracking.
  const sharingTarget = () => {
    const card = source?.closest('article');
    const cardButton = card && Array.from(card.querySelectorAll('button[data-design-id], [data-catalog-share]'))
      .find((el) => visible(el) && (el.hasAttribute('data-catalog-share') || /share/i.test(`${el.textContent} ${el.getAttribute('aria-label') || ''}`)));
    if (cardButton) return { button: cardButton };

    const localRoot = source?.closest(shareRoots);
    const roots = localRoot ? [localRoot] : Array.from(document.querySelectorAll(shareRoots));
    for (const root of roots) {
      const open = root.querySelector('[data-share-open]');
      if (visible(open)) return { button: open };
      const panel = root.querySelector('[data-share-panel]');
      const control = panel && Array.from(panel.querySelectorAll('button, a[href]')).find(visible);
      if (visible(panel) && control) return { panel, control };
    }
    return null;
  };

  // Share the page, never the raw image URL or arbitrary intake/query data.
  const pageUrl = () => {
    const current = new URL(window.location.href);
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    let result = new URL(current.pathname, current.origin);
    try {
      const candidate = new URL(canonical);
      if (candidate.origin === current.origin) result = candidate;
    } catch (_) {}
    result.search = '';
    const keys = ['variant', 'design'];
    if (document.querySelector('[data-rollercam]')) keys.push('rc', 'pair', 'slot', 'build');
    for (const key of keys) {
      if (current.searchParams.has(key)) result.searchParams.set(key, current.searchParams.get(key));
    }
    result.hash = current.hash;
    result.searchParams.set('utm_source', 'exo_share');
    result.searchParams.set('utm_medium', 'social');
    result.searchParams.set('utm_campaign', 'exo_page_share');
    return result.href;
  };

  const show = (target, focus = false) => {
    source = target instanceof Element ? target : null;
    if (notice.hidden) returnFocus = document.activeElement;
    const existing = sharingTarget();
    existingButton.hidden = !existing;
    nativeButton.hidden = !!existing || typeof navigator.share !== 'function';
    copyButton.hidden = !!existing;
    manual.hidden = true;
    status.textContent = '';
    // Keep the notice visible above an open native gallery dialog.
    const modal = document.querySelector('dialog:modal');
    (modal || document.fullscreenElement || noticeHome).appendChild(notice);
    notice.hidden = false;
    if (focus) (existing ? existingButton : nativeButton.hidden ? copyButton : nativeButton).focus();
  };

  const hide = (restoreFocus = true) => {
    const hadFocus = notice.contains(document.activeElement);
    notice.hidden = true;
    if (restoreFocus && hadFocus && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  };

  existingButton.addEventListener('click', () => {
    const target = sharingTarget();
    if (!target) { show(source, true); return; }
    hide(false);
    const modal = notice.closest('dialog:modal');
    if (modal && !modal.contains(target.button || target.panel)) modal.close();
    if (target.button) {
      target.button.focus({ preventScroll: true });
      target.button.click();
    } else {
      target.panel.scrollIntoView({ behavior: 'auto', block: 'center' });
      target.control.focus({ preventScroll: true });
    }
  });

  const manualCopy = () => {
    manual.hidden = false;
    urlInput.value = pageUrl();
    urlInput.focus();
    urlInput.select();
    status.textContent = 'Select and copy this page link to share it.';
  };

  copyButton.addEventListener('click', async () => {
    try {
      if (!navigator.clipboard?.writeText) { manualCopy(); return; }
      await navigator.clipboard.writeText(pageUrl());
      status.textContent = 'Page link copied. Ready to share.';
    } catch (_) { manualCopy(); }
  });

  nativeButton.addEventListener('click', async () => {
    try {
      await navigator.share({ title: document.title, url: pageUrl() });
      status.textContent = 'Page link shared.';
    } catch (error) {
      if (error?.name !== 'AbortError') status.textContent = 'Sharing is unavailable here. Use Copy page link.';
    }
  });

  closeButton.addEventListener('click', () => hide());
  if (pageButton) {
    pageButton.hidden = false;
    pageButton.addEventListener('click', () => show(pageButton, true));
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !notice.hidden) { hide(); return; }
    if ((event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) && !allowsNative(event)) {
      event.preventDefault();
      show(event.target, true);
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's' && !event.altKey && !allowsNative(event)) {
      event.preventDefault();
      show(event.target, true);
    }
  });

  // A stationary right-click opens the CTA on a model too. A camera pan keeps
  // its pointer events and only suppresses the browser's native context menu.
  document.addEventListener('pointerdown', (event) => {
    if (event.button !== 2 || allowsNative(event)) return;
    if (elementPath(event).some((el) => el.matches('model-viewer, canvas'))) {
      viewerGesture = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, pending: false, target: event.target };
    }
  }, true);
  document.addEventListener('pointermove', (event) => {
    if (viewerGesture?.id === event.pointerId && Math.hypot(event.clientX-viewerGesture.x, event.clientY-viewerGesture.y) > 6) viewerGesture.moved = true;
  }, true);
  document.addEventListener('pointerup', (event) => {
    if (viewerGesture?.id !== event.pointerId) return;
    const gesture = viewerGesture;
    if (gesture.pending && !gesture.moved) show(gesture.target);
    // Retain this through the same task for browsers that emit contextmenu
    // immediately after pointerup. No polling or camera-event cancellation.
    setTimeout(() => { if (viewerGesture === gesture) viewerGesture = null; }, 0);
  }, true);
  document.addEventListener('pointercancel', () => { viewerGesture = null; }, true);
  window.addEventListener('blur', () => { viewerGesture = null; });
  document.addEventListener('contextmenu', (event) => {
    if (allowsNative(event)) return;
    event.preventDefault();
    const viewer = elementPath(event).some((el) => el.matches('model-viewer, canvas'));
    if (viewer && viewerGesture) {
      viewerGesture.pending = true;
      if (event.buttons === 0 && !viewerGesture.moved) show(event.target, event.button === 0);
    } else show(event.target, event.button === 0);
  }, true);

  document.addEventListener('dragstart', (event) => {
    if (allowsNative(event)) return;
    if (elementPath(event).some((el) => el.matches(mediaSelector) || (el.matches('a') && el.querySelector(mediaSelector)))) {
      event.preventDefault();
    }
  }, true);
})();
